import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FaComments } from "react-icons/fa";
import {
  Box,
  Text,
  Avatar,
  Button,
  VStack,
  HStack,
  Input,
  Icon,
  CloseButton,
  InputGroup,
  InputRightElement,
  Flex,
} from "@chakra-ui/react";
import { IoSend } from "react-icons/io5";

const BASE_URL = process.env.REACT_APP_DEPLOYED_BASE_URL;
const BASE_UI_URL = "https://app.creativesmatter.com";

const SashaChatbot = ({
  creatorName,
  hasInteractedWithBot,
  onClose,
  setReviewContent,
  setShowReviewModal,
  showReviewModal, // <-- add this
}) => {
  const [hasInteracted, setHasInteracted] = useState(hasInteractedWithBot);
  const [messages, setMessages] = useState([]);
  const [stage, setStage] = useState("initial");
  const [accountStatus, setAccountStatus] = useState(null);
  const chatRef = useRef(null);
  const [selectedDealId, setSelectedDealId] = useState(null);
  const [showReviewLink, setShowReviewLink] = useState(false);
  const [reviewLinkClicked, setReviewLinkClicked] = useState(false);
  const token = localStorage.getItem("token");
  const [brandRep, setBrandRep] = useState("the brand rep");
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [reviewContent, setReviewContentLocal] = useState("");
  const addMessage = (from, text, showTyping = false, delay = 1500) => {
    if (from === "Sasha" && showTyping) {
      // Show typing first
      setMessages((prev) => [
        ...prev,
        {
          from: "Sasha",
          type: "typing",
        },
      ]);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev.filter((msg) => msg.type !== "typing"),
          {
            from: from,
            text: text,
            type: "text",
          },
        ]);
      }, delay);
    } else {
      setMessages((prev) => [...prev, { from, text, type: "text" }]);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      chatRef.current?.scrollTo({
        top: chatRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 100);
  };

  const showMainOptions = async () => {
    try {
      const res = await fetch(BASE_URL + "/check_account_status/", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // Ensure `token` is available in scope
        },
      });

      const data = await res.json();

      const { is_premium, has_active_subscription, current_plan, free_trial } =
        data;

      const options = [
        "Connect my email",
        "Scan my email account",
        "Track my leads",
        "Manage my membership",
        "Create Sequence",
      ];

      const isAllowedReviewContract =
        is_premium ||
        free_trial ||
        (has_active_subscription &&
          ["Administrative", "Complete"].includes(current_plan));

      if (isAllowedReviewContract) {
        options.push("Review Contract");
      }

      setTimeout(() => {
        addOptionsMessage(options);
        setStage("post-first");
      }, 2000);
    } catch (error) {
      console.error("Failed to fetch account status:", error);

      // fallback options without Review Contract
      setTimeout(() => {
        addOptionsMessage([
          "Connect my email",
          "Scan my email account",
          "Track my leads",
          "Manage my membership",
          "Create Sequence",
        ]);
        setStage("post-first");
      }, 2000);
    }
  };

  useEffect(scrollToBottom, [messages]);

  const handleUserResponse = async (response) => {
    addMessage("You", response);

    if (stage === "initial") {
      if (response === "Yes") {
        setStage("email-status");

        try {
          const token = localStorage.getItem("token");
          const res = await axios.post(
            BASE_URL + "/check-email-status/",
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (res.status === 200) {
            addMessage(
              "Sasha",
              `Great! I’ve started the process for you by creating a new scan. Click <a class="review-link" href="/profile/import">here</a> to continue the process.`
            );
            setStage("done");
          }
        } catch (error) {
          if (error.response && error.response.status === 404) {
            addMessage(
              "Sasha",
              `Awesome! I noticed that you haven’t connected your email account. Would you like to do so?`,
              true
            );
            setTimeout(() => {
              addOptionsMessage(["Yes", "No"]);
            }, 2000);
            setStage("ask-connect");
          } else {
            addMessage(
              "Sasha",
              "Oops! Something went wrong. Please try again later."
            );
            setStage("done");
          }
        }
      }

      if (response === "Tell Me More") {
        addMessage(
          "Sasha",
          `Of course! I can scan your email account to find past deals that you’ve completed and leads. Then I’ll add brands, brand reps, and create deals to help you keep track of everything. Should I create a scan for you?`,
          true
        );
        setTimeout(() => {
          addOptionsMessage(["Yes", "No"]);
        }, 2000);
        setStage("more-info");
      }

      if (response === "No, thanks") {
        addMessage(
          "Sasha",
          `Not a problem. If you change your mind, go to the <a class="review-link" href="/profile/import">Profile page</a> and click the “Import Data” tab.`,
          true
        );
        setStage("done");
      }
    }

    if (stage === "ask-connect") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Sounds good. Click <a class="review-link" href="https://app.creativesmatter.com/influencer/profile?tab=connect">here</a> to connect your email account.`,
          true
        );
      } else {
        addMessage(
          "Sasha",
          `No problem. If you want to connect your email account later, go to the <a class="review-link" href="https://app.creativesmatter.com/influencer/profile?tab=connect">Profile page</a> and click the “Connect” tab.`,
          true
        );
      }
      setStage("done");
    }

    if (stage === "more-info") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Great! I’ve started the process for you by creating a new scan. Click <a class="review-link" href="/profile/import">here</a> to continue the process.`,
          true
        );
      } else {
        addMessage(
          "Sasha",
          `Not a problem. If you change your mind, go to the <a class="review-link" href="/profile/import">Profile page</a> and click the “Import Data” tab.`,
          true
        );
      }
      setStage("done");
    }

    if (stage === "post-first") {
      // Fetch status once if not already fetched
      let api_res;
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(BASE_URL + "/check_account_status/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        api_res = res;
        setAccountStatus(res.data);
      } catch (err) {
        addMessage(
          "Sasha",
          "Sorry, I couldn’t fetch your account details. Please try again later."
        );
        return;
      }

      const {
        is_premium,
        track_leads,
        has_active_subscription,
        email_connected,
        current_plan,
      } = api_res.data;

      if (response === "Connect my email") {
        if (email_connected) {
          addMessage(
            "Sasha",
            `Your email account is currently connected. You can check the status <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=connect>here</a>.`,
            true
          );
        } else if (has_active_subscription || is_premium) {
          addMessage(
            "Sasha",
            `Sure. Click <a class="review-link"href=${BASE_UI_URL}/influencer/profile?tab=connect>here</a> to connect your account and start using the full power of the CM Hub.`,
            true
          );
        } else {
          addMessage(
            "Sasha",
            `I can definitely help you with connecting your email account, but first you’ll need to upgrade your account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>here</a> to upgrade today.`,
            true
          );
        }
        setTimeout(() => {
          showMainOptions();
        }, 1000);
      }

      if (response === "Scan my email account") {
        if (has_active_subscription || is_premium) {
          addMessage(
            "Sasha",
            `Great! I’ve started the process for you by starting a new scan. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=import>here</a> to continue the process.`,
            true
          );
        } else {
          addMessage(
            "Sasha",
            `I can definitely scan your email account, but first you’ll need to upgrade your account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>here</a> to upgrade today.`,
            true
          );
        }
        setTimeout(() => {
          showMainOptions();
        }, 1000);
      }

      if (response === "Track my leads") {
        if (!email_connected) {
          addMessage(
            "Sasha",
            `I can definitely help you track your deals, but first you need to connect your email account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=connect>here</a> to do so.`,
            true
          );
          setStage("post-first");
          setTimeout(() => {
            showMainOptions();
          }, 1000);
          return;
        }

        if (!(has_active_subscription || is_premium)) {
          addMessage(
            "Sasha",
            `I can definitely track your leads, but first you’ll need to upgrade your account. Click <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=import>here</a> to upgrade today.`,
            true
          );
          setStage("post-first");
          setTimeout(() => {
            showMainOptions();
          }, 1000);
          return;
        }

        if (email_connected && track_leads) {
          addMessage(
            "Sasha",
            `I have a secret... I’ve been tracking your leads. You haven’t noticed the new deals and brands I’ve added for you? Is this ok?`,
            true
          );
          setStage("track-leads-yesno");
          return;
        }

        if (email_connected && !track_leads) {
          addMessage(
            "Sasha",
            `I can help you track your leads moving forward. Is this ok?`,
            true
          );
          setStage("not-track-leads-yesno");
          return;
        }
      }
      if (response == "Manage my membership") {
        if (current_plan === "Free") {
          addMessage(
            "Sasha",
            `Sure. You’re currently using the free version of the CM Hub. If you <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>upgrade</a>, you’ll have full access to powerful tools and me as an assistant.`,
            true
          );
          setStage("post-first");
          setTimeout(() => {
            // showMainOptions();
          }, 1000);
        }

        if (current_plan === "Complete") {
          addMessage(
            "Sasha",
            `Sure. You currently have our “Complete” membership.`,
            true
          );
          setStage("post-first");
          setTimeout(() => {
            // showMainOptions();
          }, 1000);
        }
        if (current_plan === "Marketing" || current_plan === "Administrative") {
          addMessage(
            "Sasha",
            `Sure. You currently have our “${current_plan}” membership. Would you like to upgrade to our “Complete” membership to get access to our entire suite of powerful tools?`,
            true
          );
          setStage("upgrade-yesno");
        }
      }
      // --- Review Contract flow ---
      if (response === "Review Contract") {
        // Fetch deals and show as options
        try {
          const token = localStorage.getItem("token");
          const dealsRes = await axios.get(
            "https://api.creativesmatter.com/contract-deals/",
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          const deals = dealsRes.data.data || [];
          if (deals.length === 0) {
            addMessage("Sasha", "You have no deals available.", true);
            setStage("post-first");
            setTimeout(() => {
              showMainOptions();
            }, 1000);
            return;
          } else {
            addMessage(
              "Sasha",
              "Gladly. Which deal with a status of `contract pending` is this contract for?",
              true
            );
            // Show deal names as options
            setTimeout(() => {
              addOptionsMessage(deals.map((d) => d.deal_name));
            }, 2000);
            setStage("review-contract-select");
          }
        } catch (err) {
          addMessage(
            "Sasha",
            "Sorry, I couldn't fetch your deals. Please try again later."
          );
          setStage("post-first");
          showMainOptions();
        }
        return;
      }
      // --- End Review Contract flow ---
    }
    // --- Handle deal selection for Review Contract ---
    if (stage === "review-contract-select") {
      // Find the selected deal's ID
      try {
        const token = localStorage.getItem("token");
        const dealsRes = await axios.get(
          "https://api.creativesmatter.com/contract-deals/",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const deals = dealsRes.data.data || [];
        const selectedDealObj = deals.find((d) => d.deal_name === response);
        if (!selectedDealObj) {
          addMessage(
            "Sasha",
            "Sorry, I couldn't find that deal. Please try again.",
            true
          );
          setStage("post-first");
          setTimeout(() => {
            showMainOptions();
          }, 1000);
          return;
        }
        setSelectedDealId(selectedDealObj.deal_id);
        setSelectedDeal(selectedDealObj); // <-- store the whole deal
        setBrandRep(selectedDealObj.brand_rep);
      } catch (err) {
        addMessage(
          "Sasha",
          "Sorry, there was a problem finding your deal. Please try again."
        );
        setStage("post-first");
        showMainOptions();
        return;
      }
      addMessage("Sasha", `Thanks. Please share with me the contract.`, true);
      setTimeout(() => {
        setStage("awaiting-contract-upload");
      }, 2000);
      return;
    }
    // // --- Handle contract upload ---
    // if (stage === "awaiting-contract-upload") {
    //   // This will be handled by file input, not by button
    //   return;
    // }
    if (stage === "track-leads-yesno") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Thanks! I’ll keep tracking your leads and helping you manage things.`,
          true
        );
      } else if (response === "No") {
        addMessage(
          "Sasha",
          `Thanks for telling me. I’ll stop tracking your leads. Let me know if you ever want me to track your leads again.`,
          true
        );
        setTimeout(() => {
          showMainOptions();
        }, 1000);
      }
    }

    if (stage === "not-track-leads-yesno") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Great! I’ll keep an eye on any new leads and add the info to your CM Hub database`,
          true
        );
      } else if (response === "No") {
        addMessage(
          "Sasha",
          `No worries. Don’t hesitate to let me know if you change your mind in the future.`,
          true
        );
      }
      setTimeout(() => {
        showMainOptions();
      }, 1000);
    }
    if (stage === "upgrade-yesno") {
      if (response === "Yes") {
        addMessage(
          "Sasha",
          `Yay! Click here to <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>upgrade</a> your membership and I look forward to being your assistant`,
          true
        );
      } else {
        addMessage(
          "Ssasha",
          `No worries. If you change your mind, go to the <a class="review-link" href=${BASE_UI_URL}/influencer/profile?tab=membership>“Membership”</a> tab on the profile page. It’s super easy to do so.`,
          true
        );
      }
      // setTimeout(() => {
      //     showMainOptions();
      //   }, 1000);
      setStage("post-first");
    }
    if (response === "Create Sequence") {
      // Start sequence Q&A
      setSequenceStep(0);
      setSequenceAnswers({});
      setSequenceInput("");
      setSequenceFinished(false);
      setSequenceIsTyping(false);

      // Add the first message with typing effect
      addMessage(
        "Sasha",
        "Yay! I can definitely create an effective sequence and help you get the brand's attention. Click the Start button to begin and fill out the 3 prompts to assist me.",
        true // Enable typing effect
      );

      setStage("sequence-qna");
      return;
    }
  };

  const addOptionsMessage = (optionsArray) => {
    setMessages((prev) => [
      ...prev,
      { from: "Sasha", type: "options", options: optionsArray },
    ]);
  };

  const didSendInitialMessage = useRef(false);

  useEffect(() => {
    if (!didSendInitialMessage.current) {
      if (!hasInteracted) {
        const initialMessage = `Hi ${creatorName}! I’m Sasha, your AI assistant. Would you like me to scan your email account to help build out your CM Hub database?`;
        addMessage("Sasha", initialMessage, true);
        setTimeout(() => {
          addOptionsMessage(["Yes", "Tell Me More", "No, thanks"]);
        }, 2000);
        setStage("initial");
      } else {
        const message = `Hi ${creatorName}! How can I help you today?`;
        addMessage("Sasha", message, true);
        // Fetch account status before showing options
        setTimeout(() => {
          showMainOptions();
        }, 1000);
        setStage("post-first");
      }
      didSendInitialMessage.current = true;
    }
  }, []);

  // Helper to add a message with a clickable 'here' link
  const addReviewLinkMessage = () => {
    setMessages((prev) => [
      ...prev,
      {
        from: "Sasha",
        type: "review-link",
        text: `Sharing with you some notes I made while reviewing the contract. <a href="#" class="review-link">Click here</a> to review them.`,
      },
    ]);
    setShowReviewLink(true);
  };

  // Handle user response for Yes/No after modal close
  const handleReviewEmailResponse = (response) => {
    addMessage("You", response);
    if (response === "Yes") {
      addMessage(
        "Sasha",
        `All done. I sent ${
          brandRep || "the brand rep"
        } an email with my notes.`,
        true
      );
    } else if (response === "No") {
      addMessage("Sasha", "Ok. Let me know if you change your mind.", true);
    }
    setReviewContentLocal("");
    setStage("post-first");
    setTimeout(() => {
      showMainOptions();
    }, 1000);
  };

  // Add effect to handle review link click
  useEffect(() => {
    if (showReviewLink) {
      const handler = (e) => {
        if (e.target.classList && e.target.classList.contains("review-link")) {
          e.preventDefault();
          setShowReviewModal(true);
          setShowReviewLink(false);
          setReviewLinkClicked(true);
        }
      };
      document.addEventListener("click", handler);
      return () => document.removeEventListener("click", handler);
    }
  }, [showReviewLink, setShowReviewModal]);

  // When modal closes after review link, ask about emailing notes
  useEffect(() => {
    if (
      !showReviewModal &&
      reviewLinkClicked &&
      stage === "awaiting-review-link-click" &&
      selectedDeal &&
      selectedDeal.thread_id // Only if thread_id exists
    ) {
      addMessage(
        "Sasha",
        `Would you like me to send ${
          brandRep || "the brand rep"
        } an email with my notes?`,
        true
      );
      setStage("review-email-yesno");
      setReviewLinkClicked(false);
    }
  }, [showReviewModal, reviewLinkClicked, stage, brandRep, selectedDeal]);

  const sequencePrompts = [
    {
      id: 1,
      question:
        "Describe your content style (funny, educational, cinematic, vlog-style, etc.) and the tone or vibe you want your emails to have (e.g., professional, playful, confident, humble, etc.)",
      key: "contentStyle",
    },
    {
      id: 2,
      question:
        "Share with me the brand name and product(s) you're interested in and why you’re interested in working with the brand (e.g., you already use the product, it aligns with your values/aesthetic, your audience would love it)",
      key: "brandInterest",
    },
    {
      id: 3,
      question:
        "Provide the number of emails you would like in your sequence (we recommend 4)",
      key: "emailCount",
    },
  ];
  const [sequenceStep, setSequenceStep] = useState(0);
  const [sequenceAnswers, setSequenceAnswers] = useState({});
  const [sequenceInput, setSequenceInput] = useState("");
  const [sequenceFinished, setSequenceFinished] = useState(false);
  const [sequenceIsTyping, setSequenceIsTyping] = useState(false);

  const handleSequenceNext = () => {
    if (sequenceStep === 0) {
      setMessages((prev) => [
        ...prev,
        { from: "You", type: "text", text: "Start" },
      ]);

      // Add typing effect before showing the first prompt
      setMessages((prev) => [...prev, { from: "Sasha", type: "typing" }]);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev.filter((msg) => msg.type !== "typing"),
          { from: "Sasha", type: "text", text: sequencePrompts[0].question },
        ]);
        setSequenceStep(1);
        setSequenceInput("");
      }, 1500); // Adjust delay as needed
      return;
    }

    const currentPrompt = sequencePrompts[sequenceStep - 1];
    const answerKey = currentPrompt.key;
    const updatedAnswers = { ...sequenceAnswers, [answerKey]: sequenceInput };
    setSequenceAnswers(updatedAnswers);

    const isLast = sequenceStep === sequencePrompts.length;
    const newHistory = [{ from: "You", type: "text", text: sequenceInput }];

    if (isLast) {
      const emailCount = sequenceInput || "a few";
      if (answerKey === "emailCount") {
        const count = parseInt(sequenceInput, 10);
        if (isNaN(count) || count < 1 || count > 4) {
          setMessages((prev) => [
            ...prev,
            { from: "You", type: "text", text: sequenceInput },
            {
              from: "Sasha",
              type: "text",
              text: "Please enter a valid number between 1 and 4.",
            },
          ]);
          setSequenceInput("");
          return;
        }
      }

      newHistory.push({
        from: "Sasha",
        type: "text",
        text: `Thanks for the help. I'll create a ${emailCount}-email sequence tailored to your needs.`,
      });

      setSequenceFinished(true);
      setSequenceIsTyping(true);

      setMessages((prev) => [
        ...prev,
        ...newHistory,
        { from: "Sasha", type: "typing", text: "Sasha is typing..." },
      ]);

      setSequenceInput("");

      // API call logic remains unchanged
      const payload = {
        content_style: updatedAnswers.contentStyle,
        product_info: updatedAnswers.brandInterest,
        email_count: Number(updatedAnswers.emailCount),
      };
      const token = localStorage.getItem("token");
      fetch(BASE_URL + "/auto_sequence_generator/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
        .then((res) => res.json())
        .then((data) => {
          setSequenceIsTyping(false);
          setMessages((prev) => [
            ...prev.filter((msg) => msg.type !== "typing"),
            {
              from: "Sasha",
              type: "text",
              text:
                data?.message ||
                "Thanks for the help. Give me a few minutes to generate your sequence.",
            },
          ]);
          setTimeout(() => {
            showMainOptions();
          }, 2000);
          setStage("post-first");
        })
        .catch((err) => {
          setSequenceIsTyping(false);
          setMessages((prev) => [
            ...prev.filter((msg) => msg.type !== "typing"),
            {
              from: "Sasha",
              type: "text",
              text: "Oops! Something went wrong while generating your sequence. Please try again later.",
            },
          ]);
          setTimeout(() => {
            showMainOptions();
          }, 2000);
          setStage("post-first");
        });
    } else {
      const nextPrompt = sequencePrompts[sequenceStep];

      // Add typing effect before showing the next prompt
      setMessages((prev) => [
        ...prev,
        ...newHistory,
        { from: "Sasha", type: "typing" },
      ]);

      setTimeout(() => {
        setMessages((prev) => [
          ...prev.filter((msg) => msg.type !== "typing"),
          { from: "Sasha", type: "text", text: nextPrompt.question },
        ]);
        setSequenceInput("");
        setSequenceStep(sequenceStep + 1);
      }, 1500); // Adjust delay as needed
    }
  };

  return (
    <div className="sasha-chatbox">
      <div className="sasha-chatbox-header">
        <Box bg="#1e1917" px={3} py={2} borderTopRadius="md">
          <Flex align="center">
            <Flex align="center" justify="center" flex="1">
              <Icon as={FaComments} color="white" mr={2} />
              <Text fontWeight="bold" color="white">
                Sasha Chat
              </Text>
            </Flex>
          </Flex>
        </Box>
      </div>

      <div className="sasha-chatbox-body" ref={chatRef}>
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`sasha-chatbox-message-wrapper ${
              msg.from === "You" ? "user" : "sasha"
            }`}
          >
            {msg.type !== "options" && msg.type !== "review-link" && (
              <div
                className="user-type"
                style={{
                  fontSize: "12px",
                  color: "#999",
                  marginBottom: "4px",
                  marginRight: "11px",
                  textAlign: msg.from === "You" ? "right" : "left",
                }}
              >
                {msg.from}
              </div>
            )}

            <div className="sasha-chatbox-message">
              {msg.from === "Sasha" && msg.type !== "options" && (
                <img
                  src="/images/SashaImage.jpeg"
                  alt="Sasha"
                  className="sasha-avatar"
                />
              )}

              {msg.type === "options" ? (
                <div className="sasha-options">
                  <div
                    style={{
                      marginBottom: "6px",
                      fontSize: "13px",
                      color: "#ccc",
                    }}
                  >
                    Choose a reply
                  </div>
                  {msg.options.map((option, i) => (
                    <button
                      key={i}
                      className="sasha-option-button"
                      onClick={() => handleUserResponse(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              ) : msg.type === "review-link" ? (
                <div
                  className={`sasha-bubble sasha`}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              ) : msg.type === "typing" ? (
                <div className="sasha-bubble sasha typing-indicator">
                  <span>.</span>
                  <span>.</span>
                  <span>.</span>
                </div>
              ) : (
                <div
                  className={`sasha-bubble ${
                    msg.from === "You" ? "user" : "sasha"
                  }`}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="sasha-chatbox-footer">
        {stage === "sequence-qna" &&
          sequenceStep > 0 &&
          sequenceStep <= sequencePrompts.length &&
          !sequenceFinished && (
            <div style={{ display: "flex", gap: 8, width: "100%" }}>
              <Input
                placeholder="Compose your message..."
                value={sequenceInput}
                onChange={(e) => setSequenceInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && sequenceInput.trim()) {
                    handleSequenceNext();
                  }
                }}
                borderRadius="md"
                style={{ flex: 1 }}
              />
              <Button
                h="1.75rem"
                size="sm"
                onClick={handleSequenceNext}
                isDisabled={!sequenceInput.trim()}
                bg="black"
                _hover={{ bg: "gray.700" }}
                color="white"
                borderRadius="md"
              >
                <IoSend size="16" />
              </Button>
            </div>
          )}
        {/* Sequence Q&A start button */}
        {stage === "sequence-qna" && sequenceStep === 0 && (
          <div style={{ textAlign: "right" }}>
            <Button size="sm" colorScheme="blue" onClick={handleSequenceNext}>
              Start
            </Button>
          </div>
        )}
        {/* Prevent interaction while modal is open and awaiting review link click */}
        {!(showReviewModal && stage === "awaiting-review-link-click") &&
          stage !== "sequence-qna" && (
            <>
              {/* Only show Yes/No if stage is review-email-yesno AND selectedDeal has thread_id */}
              {stage === "review-email-yesno" &&
                selectedDeal &&
                selectedDeal.thread_id && (
                  <>
                    <button onClick={() => handleReviewEmailResponse("Yes")}>
                      Yes
                    </button>
                    <button onClick={() => handleReviewEmailResponse("No")}>
                      No
                    </button>
                  </>
                )}
              {stage === "track-leads-yesno" && (
                <>
                  <button onClick={() => handleUserResponse("Yes")}>Yes</button>
                  <button onClick={() => handleUserResponse("No")}>No</button>
                </>
              )}
              {stage === "not-track-leads-yesno" && (
                <>
                  <button onClick={() => handleUserResponse("Yes")}>Yes</button>
                  <button onClick={() => handleUserResponse("No")}>No</button>
                </>
              )}
              {stage === "upgrade-yesno" && (
                <>
                  <button onClick={() => handleUserResponse("Yes")}>Yes</button>
                  <button onClick={() => handleUserResponse("No")}>No</button>
                </>
              )}
              {stage === "awaiting-contract-upload" && (
                <ContractUploadInput
                  onUpload={async (file) => {
                    const trimmedName =
                      file.name.length > 10
                        ? file.name.slice(0, 10) + "..."
                        : file.name;
                    addMessage("You", `Uploaded: ${trimmedName}`);
                    addMessage(
                      "Sasha",
                      "Great! Give me a few minutes while I review the contract and provide feedback.",
                      true
                    );
                    setTimeout(() => {
                      setMessages((prev) => [
                        ...prev,
                        {
                          from: "Sasha",
                          type: "typing", // custom type
                        },
                      ]);
                    }, 2000);

                    try {
                      const token = localStorage.getItem("token");
                      const formData = new FormData();
                      formData.append("file", file);
                      formData.append("deal_id", selectedDealId);
                      const gptRes = await axios.post(
                        "https://api.creativesmatter.com/review-contract/",
                        formData,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "multipart/form-data",
                          },
                        }
                      );
                      setMessages((prev) =>
                        prev.filter((msg) => msg.type !== "typing")
                      );

                      addReviewLinkMessage();
                      setReviewContentLocal(gptRes.data?.review?.response);
                      if (typeof setReviewContent === "function")
                        setReviewContent(gptRes.data?.review?.response);
                      // Only proceed to review link click if deal has thread_id
                      if (selectedDeal && selectedDeal.thread_id) {
                        setStage("awaiting-review-link-click");
                      } else {
                        // No thread_id: just end the flow after showing GPT response
                        setStage("done");
                      }
                    } catch (err) {
                      setMessages((prev) =>
                        prev.filter(
                          (msg) =>
                            msg.text !==
                            "Reviewing contract... (this may take a moment)"
                        )
                      );
                      addMessage(
                        "Sasha",
                        "Sorry, there was a problem uploading or reviewing your contract. Please try again."
                      );
                    }
                  }}
                />
              )}
              {!showReviewModal && reviewContent && (
                <div>
                  <Button
                    size="sm"
                    colorScheme="teal"
                    onClick={() => setShowReviewModal(true)}
                  >
                    View Notes
                  </Button>
                </div>
              )}
            </>
          )}
      </div>
    </div>
  );
};

export default SashaChatbot;

// ContractuploadInput component
function ContractUploadInput({ onUpload }) {
  const fileInputRef = useRef();
  const [isFileUploaded, setIsFileUploaded] = useState(false);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <input
        type="file"
        accept=".pdf,.doc,.docx"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files[0];
          if (file) {
            onUpload(file);
            setIsFileUploaded(true);
          }
          e.target.value = ""; // reset input
        }}
      />
      {!isFileUploaded && (
        <button
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          style={{ padding: "6px 12px", fontSize: 14 }}
        >
          Upload Contract
        </button>
      )}
    </div>
  );
}
